import { yupResolver } from "@hookform/resolvers/yup";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { i18_TRANSLATION } from "../../i18nReferences";
import { getHelperText } from "../../yupLocalization";
import { createResetPasswordSchema } from "./resetPasswordSchema";

/**
 * Form for the new password after the link from the mail. Opening the page changes nothing; only the
 * submit sends the new password.
 */
const ResetPasswordView = ({ t, onSubmit, isSubmitDisabled, isSuccess, isFailed, onBackToLogin }) => {
    const schema = useMemo(() => createResetPasswordSchema(), []);
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: "onTouched",
        resolver: yupResolver(schema),
    });

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Box sx={{ maxWidth: "500px", width: "100%", px: 2, pb: 4 }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            required
                            type="password"
                            variant="standard"
                            autoComplete="new-password"
                            {...register("newPassword")}
                            error={!!errors?.newPassword}
                            helperText={getHelperText(t, errors, "newPassword")}
                            disabled={isSuccess}
                            label={t("STUDIO_LOGIN.PASSWORD_LABEL", { ns: i18_TRANSLATION })}
                        />
                        <TextField
                            fullWidth
                            required
                            type="password"
                            variant="standard"
                            autoComplete="new-password"
                            {...register("newPasswordConfirmation")}
                            error={!!errors?.newPasswordConfirmation}
                            helperText={getHelperText(t, errors, "newPasswordConfirmation")}
                            disabled={isSuccess}
                            label={t("RESET_PASSWORD.CONFIRMATION_LABEL", { ns: i18_TRANSLATION, defaultValue: "Neues Passwort wiederholen" })}
                        />
                        <Box>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitDisabled || isSuccess}
                                data-testid="reset-password-submit"
                            >
                                {t("STUDIO_LOGIN.RESET_PASSWORD", { ns: i18_TRANSLATION })}
                            </Button>
                        </Box>
                        {isFailed ? (
                            <Alert severity="error">{t("RESET_PASSWORD.FAILED_INFO", { ns: i18_TRANSLATION, defaultValue: "Der Link ist ungültig oder abgelaufen. Fordern Sie im Login einen neuen Link an." })}</Alert>
                        ) : null}
                        {isSuccess ? (
                            <Alert severity="success">{t("RESET_PASSWORD.SUCCESS_INFO", { ns: i18_TRANSLATION, defaultValue: "Ihr Passwort wurde geändert. Sie können sich jetzt anmelden." })}</Alert>
                        ) : null}
                        {isSuccess || isFailed ? (
                            <Link onClick={onBackToLogin} sx={{ cursor: "pointer" }}>
                                {t("STUDIO_LOGIN.LOGIN_BUTTON", { ns: i18_TRANSLATION })}
                            </Link>
                        ) : null}
                    </Box>
                </form>
            </Box>
        </Box>
    );
};

export default ResetPasswordView;
